import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'

type AppTheme = 'light' | 'dark'

type AppThemeContextValue = {
  theme: AppTheme
  isDarkMode: boolean
  toggleTheme: () => void
}

const APP_THEME_STORAGE_KEY = 'school-erp-theme'
const CTA_PROXIMITY_RADIUS = 96
const CTA_PROXIMITY_VARIABLE = '--cta-proximity'
const CTA_SELECTOR = "button:not(:disabled):not([aria-label]):not([data-no-cta-proximity='true'])"

const AppThemeContext = createContext<AppThemeContextValue | null>(null)

function getInitialTheme(): AppTheme {
  return 'light'
}

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<AppTheme>(getInitialTheme)

  useEffect(() => {
    document.documentElement.dataset.theme = 'light'
    document.documentElement.classList.remove('school-admin-theme-dark')
    window.localStorage.setItem(APP_THEME_STORAGE_KEY, 'light')
  }, [theme])

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (prefersReducedMotion) {
      return
    }

    let frameId = 0
    let pointerX = 0
    let pointerY = 0
    let hasPointerPosition = false

    const clearButtonProximity = () => {
      document
        .querySelectorAll<HTMLButtonElement>(CTA_SELECTOR)
        .forEach((button) => button.style.removeProperty(CTA_PROXIMITY_VARIABLE))
    }

    const applyButtonProximity = () => {
      frameId = 0

      if (!hasPointerPosition) {
        clearButtonProximity()
        return
      }

      document.querySelectorAll<HTMLButtonElement>(CTA_SELECTOR).forEach((button) => {
        const rect = button.getBoundingClientRect()
        const deltaX = Math.max(rect.left - pointerX, 0, pointerX - rect.right)
        const deltaY = Math.max(rect.top - pointerY, 0, pointerY - rect.bottom)
        const distance = Math.hypot(deltaX, deltaY)
        const proximity = Math.max(0, Math.min(1, 1 - distance / CTA_PROXIMITY_RADIUS))

        if (proximity > 0) {
          button.style.setProperty(CTA_PROXIMITY_VARIABLE, proximity.toFixed(3))
        } else {
          button.style.removeProperty(CTA_PROXIMITY_VARIABLE)
        }
      })
    }

    const scheduleUpdate = () => {
      if (frameId === 0) {
        frameId = window.requestAnimationFrame(applyButtonProximity)
      }
    }

    const handlePointerMove = (event: PointerEvent) => {
      pointerX = event.clientX
      pointerY = event.clientY
      hasPointerPosition = true
      scheduleUpdate()
    }

    const handlePointerLeave = () => {
      hasPointerPosition = false
      clearButtonProximity()
    }

    window.addEventListener('pointermove', handlePointerMove, { passive: true })
    window.addEventListener('scroll', scheduleUpdate, { passive: true, capture: true })
    window.addEventListener('resize', scheduleUpdate)
    window.addEventListener('blur', handlePointerLeave)
    document.addEventListener('pointerleave', handlePointerLeave)

    return () => {
      if (frameId !== 0) {
        window.cancelAnimationFrame(frameId)
      }

      clearButtonProximity()
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('scroll', scheduleUpdate, true)
      window.removeEventListener('resize', scheduleUpdate)
      window.removeEventListener('blur', handlePointerLeave)
      document.removeEventListener('pointerleave', handlePointerLeave)
    }
  }, [])

  return (
    <AppThemeContext.Provider
      value={{
        theme,
        isDarkMode: theme === 'dark',
        toggleTheme: () => setTheme((current) => (current === 'dark' ? 'light' : 'dark')),
      }}
    >
      {children}
    </AppThemeContext.Provider>
  )
}

export function useAppTheme() {
  const context = useContext(AppThemeContext)

  if (!context) {
    throw new Error('useAppTheme must be used inside AppThemeProvider')
  }

  return context
}
